import { getContrastRatio } from '@chronosphereio/chrono-ui';
import { LineSeriesOption } from 'echarts/charts';
import { useMemo } from 'react';
import { ChartsTheme, StatChartComparison, useChartsTheme } from '@/components';
import {
  SingleValue,
  SingleValueTimeSeries,
  StatChartSpec,
  StatChartStepOption,
  StatChartThresholdOptions,
} from '@/core';
import { findThreshold } from '@/core/utils/thresholds';
import { UseDataQueryResult } from '@/plugin-system';
import { StatChartPanelProps } from '../StatChartPanel';
import { CombinedStatChartData, StatChartColorScheme, StatChartPanelLoadedProps } from '../stat-chart-model';

export function getColorThemeFromThresholds(
  chartsTheme: ChartsTheme,
  thresholds?: StatChartThresholdOptions,
  value?: number | null
) {
  // thresholds color takes priority over other colors
  const defaultColor = thresholds?.base?.color ?? thresholds?.default_color ?? chartsTheme.thresholds.defaultColor;
  const defaultBackgroundColor = thresholds?.base?.background_color;

  if (thresholds === undefined) {
    return { color: defaultColor, backgroundColor: defaultBackgroundColor };
  }

  let color = defaultColor;
  let backgroundColor: string | undefined = defaultBackgroundColor;

  if (value !== null && typeof value === 'number') {
    const threshold = findThreshold({ thresholds, value, palette: chartsTheme.thresholds });

    if (threshold.color) {
      color = threshold.color;
    }

    backgroundColor = threshold.background_color;
  }

  return { color, backgroundColor };
}

type convertSparklineProps = Pick<StatChartPanelProps, 'thresholds' | 'sparkline' | 'colorScheme'> & {
  chartsTheme: ChartsTheme;
  isComparisonData?: boolean;
  value?: number | null;
};

export function convertSparkline({
  chartsTheme,
  sparkline,
  thresholds,
  value,
  isComparisonData,
  colorScheme,
}: convertSparklineProps): LineSeriesOption | undefined {
  if (sparkline === undefined) return;

  let color = sparkline.color;
  let background: undefined | string = undefined;

  if (colorScheme === StatChartColorScheme.THRESHOLDS) {
    const { color: themeColor, backgroundColor: themeBackground } = getColorThemeFromThresholds(
      chartsTheme,
      thresholds,
      value
    );
    color = themeColor;
    background = themeBackground;
  }

  const hasBackground = background !== undefined;
  const fillColor = hasBackground ? background : color;

  const markLine: LineSeriesOption['markLine'] = { data: [] };

  if (sparkline.show_thresholds === true && thresholds?.steps !== undefined && thresholds.steps.length > 0) {
    let marklineColor = chartsTheme.theme.palette.text.legacySecondary;

    if (hasBackground && fillColor) {
      try {
        const secondaryTextRatio = getContrastRatio(chartsTheme.theme.palette.text.legacySecondary, fillColor);
        if (secondaryTextRatio > chartsTheme.theme.palette.contrastThreshold) {
          marklineColor = chartsTheme.theme.palette.text.inversePrimary;
        }
      } catch (_e) {
        // getContrastRatio can throw if format is not proper hex (like a color name for instance)
        // just swallow this error and live with the secondary text color
      }
    }

    for (const step of thresholds.steps) {
      if (step.value !== undefined) {
        markLine.data?.push({
          yAxis: step.value,
          lineStyle: {
            color: marklineColor,
            type: 'dashed',
            width: 1,
          },
        });
      }
    }
  }

  return {
    lineStyle: {
      width: sparkline.width ?? chartsTheme.sparkline.width,
      color: isComparisonData ? 'transparent' : color,
      opacity: 1,
    },
    areaStyle: {
      color: fillColor,
      opacity: 0.4,
    },
    markLine,
    ...(isComparisonData && {
      itemStyle: {
        decal: chartsTheme.stripedDecal,
      },
    }),
  };
}

export function isComparisonTimeSeries(series: SingleValue | SingleValueTimeSeries) {
  return series.config?.compareTo !== undefined && series.config?.compareTo !== null;
}

export function convertToStatChartData(
  data: SingleValue | SingleValueTimeSeries,
  { chartsTheme, sparkline, thresholds, colorScheme }: UseStatChartDataOpts & { chartsTheme: ChartsTheme }
): CombinedStatChartData {
  const isComparison = isComparisonTimeSeries(data);
  const calculatedValue = data.value;
  const { color } = getColorThemeFromThresholds(chartsTheme, thresholds, calculatedValue);
  return {
    seriesData: {
      name: data.formattedName || data.name,
      values: (data as SingleValueTimeSeries).timeSeries ?? [],
    },
    calculatedValue,
    color,
    sparkline: convertSparkline({
      chartsTheme,
      sparkline,
      thresholds,
      value: calculatedValue,
      isComparisonData: isComparison,
      colorScheme,
    }),
  };
}

/**
 * Time series are returned as array and we need to group baseline & comparison time series together.
 * If we find a comparison time series and have its baseline time series, link the two together.
 * Otherwise, save it until we encounter its baseline further in array.
 */
export function saveOrLinkComparisonSeries({
  statChart,
  statChartData,
  compareTo,
  seriesIndexToChartDataIndex,
  compareToIndexToChartData,
}: {
  compareTo?: number | null;
  statChart: CombinedStatChartData;
  statChartData: CombinedStatChartData[];
  seriesIndexToChartDataIndex: Record<number, number>;
  compareToIndexToChartData: Record<number, StatChartComparison>;
}) {
  if (typeof compareTo !== 'number') return;

  const chartDataIndex = seriesIndexToChartDataIndex[compareTo];
  const baselineSeries = chartDataIndex !== undefined ? statChartData[chartDataIndex] : undefined;
  if (baselineSeries && chartDataIndex !== undefined) {
    statChartData[chartDataIndex] = { ...baselineSeries, comparison: statChart };
  } else {
    compareToIndexToChartData[compareTo] = statChart;
  }
}

/**
 * Helper function for determining if we should show the series name
 * on the StatChart.
 * We want to show the series name if
 * 1) the spec has set the show_series_name value to Always
 * OR
 * 2) the spec is set to auto and there is more than 1 chart
 */
export const shouldShowSeriesName = (
  showSeriesName: NonNullable<StatChartSpec['show_series_name']>,
  numberOfCharts: number
) => {
  return showSeriesName === 'Always' || (showSeriesName === 'Auto' && numberOfCharts > 1);
};

/**
 * If we are always showing the series name and the threshold is hiding the value
 * and doesn't have a message, we should use the series name as the message.
 */
export const shouldUseSeriesNameAsMessage = (
  shouldShowSeriesName: boolean,
  threshold: StatChartStepOption | NonNullable<StatChartThresholdOptions['base']>
) => {
  return threshold.show_value === false && !threshold.name && shouldShowSeriesName;
};

/**
 * Calculates display properties for a stat chart series based on thresholds.
 * Determines the threshold, whether to show series name as message, and the final
 * calculated showSeriesName value.
 */
export function calculateSeriesDisplayProps({
  calculatedValue,
  findThreshold,
  showSeriesName,
}: {
  calculatedValue: number | null | undefined;
  findThreshold: (value: number) => StatChartStepOption | NonNullable<StatChartThresholdOptions['base']>;
  showSeriesName: boolean;
}) {
  const threshold = findThreshold(calculatedValue || 0);
  const showSeriesNameAsMessage = shouldUseSeriesNameAsMessage(showSeriesName, threshold);
  const calculatedShowSeriesName = showSeriesNameAsMessage ? false : showSeriesName;

  return {
    threshold,
    showSeriesNameAsMessage,
    calculatedShowSeriesName,
  };
}

export type UseStatChartDataOpts = Pick<
  StatChartPanelLoadedProps,
  'calculation' | 'thresholds' | 'sparkline' | 'colorScheme'
>;

export interface UseStatChartDataResult {
  statChartData: CombinedStatChartData[];
  /**
   * Dashed reference series sourced from threshold-flagged `SingleValueTimeSeries` queries.
   */
  referenceSeries: LineSeriesOption[];
}

/**
 * Builds the per-tile data for the StatChart, excluding any series that the
 * per-query threshold wrapper has tagged via `config.threshold`.
 */
export const useStatChartData = (
  queryResults: UseDataQueryResult<'SingleValue' | 'SingleValueTimeSeries'>['queryResults'],
  { calculation, thresholds, sparkline, colorScheme }: UseStatChartDataOpts
): UseStatChartDataResult => {
  const chartsTheme = useChartsTheme();

  return useMemo(() => {
    const statChartData: CombinedStatChartData[] = [];
    const referenceSeries: LineSeriesOption[] = [];
    for (const result of queryResults) {
      // Skip queries that are still loading or don't have data
      if (result.isLoading || result.isFetching || result.data === undefined) continue;

      const seriesIndexToChartDataIndex: Record<number, number> = {};
      const compareToIndexToChartData: Record<number, StatChartComparison> = {};
      for (let seriesIndex = 0; seriesIndex < result.data.values.length; seriesIndex++) {
        const seriesData = result.data.values[seriesIndex];
        if (seriesData === undefined) continue;

        // Per-query threshold style: capture the flagged series' timeline as a
        // dashed reference line that the panel overlays on every tile, instead
        // of rendering it as its own tile. The flagged series should not
        // influence value calculation, color, or background.
        const queryThreshold = seriesData.config?.threshold;
        const seriesTimeline = (seriesData as SingleValueTimeSeries).timeSeries;
        if (queryThreshold !== undefined) {
          // Only build the dashed reference overlay when there's a primary sparkline to
          // overlay it on.
          if (sparkline !== undefined && Array.isArray(seriesTimeline) && seriesTimeline.length > 0) {
            referenceSeries.push({
              type: 'line',
              name: queryThreshold.label ?? seriesData.formattedName ?? seriesData.name,
              data: seriesTimeline,
              symbol: 'none',
              silent: true,
              animation: false,
              lineStyle: {
                type: 'dashed',
                color: queryThreshold.color,
                width: chartsTheme.sparkline.width,
                opacity: 1,
              },
            });
          }
          continue;
        }

        const statChart = convertToStatChartData(seriesData, {
          chartsTheme,
          thresholds,
          sparkline,
          calculation,
          colorScheme,
        });

        if (isComparisonTimeSeries(seriesData)) {
          saveOrLinkComparisonSeries({
            compareTo: seriesData.config?.compareTo,
            statChart,
            statChartData,
            seriesIndexToChartDataIndex,
            compareToIndexToChartData,
          });
          continue;
        }

        statChartData.push({ ...statChart, comparison: compareToIndexToChartData[seriesIndex] });
        seriesIndexToChartDataIndex[seriesIndex] = statChartData.length - 1;
      }
    }
    return { statChartData, referenceSeries };
  }, [queryResults, chartsTheme, thresholds, sparkline, calculation, colorScheme]);
};
